import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const HERO = "https://images.pexels.com/photos/4172645/pexels-photo-4172645.jpeg?auto=compress&cs=tinysrgb&w=2400";
const SERIES = [
  ["Relocation Guides", "Three-volume country guides, toolkits and planners for Australians moving abroad."],
  ["Small Towns Series", "Researched town guides and offline toolkits across eight European countries."],
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#1C181D]" data-testid="login-page">
      <img src={HERO} alt="Vernazza harbour at sunset" className="absolute inset-0 w-full h-full object-cover login-kenburns" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1C181D]/90 via-[#3B1B48]/65 to-[#1C181D]/40" />
      <div className="relative z-10 min-h-screen max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-10 grid lg:grid-cols-[1.2fr_440px] gap-12 items-center">
        <div className="text-[#FAF7F2] rise">
          <p className="eyebrow mb-6">The Expat Studio · Guide studio</p>
          <h1 data-testid="login-headline" className="font-display text-5xl sm:text-6xl xl:text-7xl leading-[1.02] max-w-2xl">Research it.<br />Write it.<br /><span className="italic text-[#C59B27]">Sell it.</span></h1>
          <div className="mt-12 grid sm:grid-cols-2 gap-8 max-w-2xl">
            {SERIES.map(([t, d], i) => (
              <div key={t} className="border-l-2 border-[#C59B27] pl-5 rise" style={{ animationDelay: `${0.15 + i * 0.12}s` }} data-testid={`login-series-${i}`}>
                <p className="font-display text-2xl">{t}</p>
                <p className="mt-2 text-sm text-[#EFE6F3]/85 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-14 font-mono-accent text-[10px] tracking-[0.22em] text-[#EFE6F3]/60 hidden sm:block">PART OF ANTELLIHQ · PUBLISHED ON THEEXPATSTUDIO.COM</p>
        </div>
        <form onSubmit={submit} className="w-full backdrop-blur-xl border border-white/40 shadow-2xl p-8 sm:p-10 rise" style={{ animationDelay: "0.2s", background: "rgba(250,247,242,0.94)" }} data-testid="login-form">
          <img src="/logo.png" alt="The Expat Studio" data-testid="login-logo" className="h-16 w-auto mb-8" />
          <p className="eyebrow mb-2">Relocation and Small Towns</p>
          <h2 className="font-display text-4xl text-[#3B1B48] mb-8">Sign in</h2>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white rounded-none" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 bg-white rounded-none" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-700">{error}</p>}
            <Button data-testid="login-submit-button" disabled={busy} className="w-full h-12 rounded-none tracking-wide">
              {busy ? "Signing in…" : "Enter the studio"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
